// src/pages/ResetPasswordPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiLock, FiArrowLeft, FiCheck, FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { resetAdminPassword } from "../../redux/actions/AdminActions";
import { toast } from "react-hot-toast";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    const storedName = localStorage.getItem("name") || "User";
    
    if (storedUserId) {
      setUserId(storedUserId);
    }
    if (storedName) {
      setUserName(storedName);
    }
  }, []);

  // Check password strength
  useEffect(() => {
    const newPassword = formData.newPassword;
    setPasswordStrength({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    });
  }, [formData.newPassword]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Prepare payload based on your API requirements
      const payload = {
        admin_id: userId,
        new_password: formData.newPassword
      };

      // Dispatch the reset password action
      await dispatch(resetAdminPassword(payload, () => {
        // Success callback
        toast.success("Password reset successfully!");
        
        // Clear form
        setFormData({
          newPassword: "",
          confirmPassword: "",
        });
        
        // Navigate back after 2 seconds
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 2000);
      }));

    } catch (error) {
      toast.error("Failed to reset password. Please try again.");
      console.error("Password reset error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrengthScore = () => {
    const checks = Object.values(passwordStrength);
    const passed = checks.filter(Boolean).length;
    return Math.floor((passed / checks.length) * 100);
  };

  const getStrengthColor = () => {
    const score = getPasswordStrengthScore();
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    if (score >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  const getStrengthText = () => {
    const score = getPasswordStrengthScore();
    if (score >= 80) return "Strong";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Weak";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <FiArrowLeft />
          Back to Dashboard
        </button>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-blue-600 to-cyan-600">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <FiLock className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Reset Password</h1>
                <p className="text-blue-100 mt-1">
                  For user: <span className="font-semibold">{userName}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword.new ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    className={`w-full px-4 py-3 pl-10 bg-gray-50 dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.newPassword 
                        ? "border-red-500 focus:ring-red-500" 
                        : "border-gray-200 dark:border-gray-600"
                    } text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                    disabled={loading}
                  />
                  <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("new")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  >
                    {showPassword.new ? (
                      <FiEyeOff className="text-sm" />
                    ) : (
                      <FiEye className="text-sm" />
                    )}
                  </button>
                </div>
                
                {/* Password Strength Meter */}
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Password strength
                    </span>
                    <span className={`text-xs font-medium ${
                      getPasswordStrengthScore() >= 80 ? "text-green-600 dark:text-green-400" :
                      getPasswordStrengthScore() >= 60 ? "text-yellow-600 dark:text-yellow-400" :
                      getPasswordStrengthScore() >= 40 ? "text-orange-600 dark:text-orange-400" :
                      "text-red-600 dark:text-red-400"
                    }`}>
                      {getStrengthText()}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getStrengthColor()} transition-all duration-300`}
                      style={{ width: `${getPasswordStrengthScore()}%` }}
                    ></div>
                  </div>
                </div>

                {errors.newPassword && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                    <FiX className="text-sm" />
                    {errors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword.confirm ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    className={`w-full px-4 py-3 pl-10 bg-gray-50 dark:bg-gray-700 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      errors.confirmPassword 
                        ? "border-red-500 focus:ring-red-500" 
                        : "border-gray-200 dark:border-gray-600"
                    } text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                    disabled={loading}
                  />
                  <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("confirm")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  >
                    {showPassword.confirm ? (
                      <FiEyeOff className="text-sm" />
                    ) : (
                      <FiEye className="text-sm" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                    <FiX className="text-sm" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Password Requirements:
                </p>
                <ul className="space-y-2">
                  {[
                    { key: 'length', text: 'At least 8 characters long' },
                    { key: 'uppercase', text: 'One uppercase letter (A-Z)' },
                    { key: 'lowercase', text: 'One lowercase letter (a-z)' },
                    { key: 'number', text: 'One number (0-9)' },
                    { key: 'special', text: 'One special character (!@#$%^&*)' },
                  ].map((req) => (
                    <li key={req.key} className="flex items-center gap-2">
                      {passwordStrength[req.key] ? (
                        <FiCheck className="text-green-500" />
                      ) : (
                        <FiX className="text-gray-400" />
                      )}
                      <span className={`text-xs ${
                        passwordStrength[req.key] 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {req.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <FiArrowLeft />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || getPasswordStrengthScore() < 40}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <FiLock />
                      Reset Password
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Note:</strong> After resetting your password, you'll remain logged in. 
            For security reasons, consider logging out and logging back in with your new password.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;